import { Box, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
  filteredChatDashboardData,
  getAverageMessages,
  getAverageSatisfaction,
  getAverageSeconds,
  getConversationsPerMonth,
  getTotalConversations,
  getTotalMessages,
  getUniqueChatConversations,
} from '../../../helpers/chatDashboardHelper';
import { generateUniqueKey } from '../../../helpers/app';
import { useDashboardChatAgents } from '../../../hooks/queries/useDashboardChatAgents';
import { useChatDashboardData } from '../../../hooks/queries/useChatDashboardData';
import { useTopChatAgents } from '../../../hooks/queries/useTopChatAgents';
import { useCategoryChips } from '../../../hooks/useCategoryChips';
import { useDashboardTableState } from '../../../hooks/useDashboardTableState';
import {
  getCountsPerDay,
  getTotalRows,
} from '../../../helpers/dashboardHelper';
import { useErrorToast } from '../../../hooks/useErrorToast';
import { useIsRefreshing } from '../../../hooks/useIsRefreshing';
import { useTranslation } from '../../../hooks/useTranslation';
import useAuth from '../../../hooks/useAuth';
import { AgentType } from '../../../interfaces/Agent';
import { ChatAgent } from '../../../interfaces/ChatAgent';
import { Conversation, DateRangeDate } from '../../../interfaces/Analytics';
import ChatAgentTopThree from '../../shared/ChatAgentTopThree/ChatAgentTopThree';
import BarChart from '../../shared/BarChart/BarChart';
import CategoryFilter from '../../shared/CategoryFilter/CategoryFilter';
import ConversationsTable from '../../shared/ConversationsTable/ConversationsTable';
import LiveSyncStoppedModal from '../../shared/LiveSyncToggle/LiveSyncStoppedModal';
import ChatOverAllMetrics from '../../shared/ChatOverAllMetrics/ChatOverAllMetrics';
import StyledTopThreeAgents from './StyledTopThreeAgents';

interface MultiChatAgentsDashboardProps {
  dateRange: [DateRangeDate, DateRangeDate];
  selectedAgent: string;
  showLimitModal: boolean;
  setShowLimitModal: (value: boolean) => void;
  onRefetchNeeded?: (refetch: () => void) => void;
  onLoadingStateChange?: (isLoading: boolean) => void;
}

const fillOutAgentNames = (
  conversations: Conversation[],
  agentsList: ChatAgent[],
  unknownAgentLabel: string
): Conversation[] => {
  return conversations.map(conversation => {
    const agent = agentsList.find(agent => agent.id === conversation.agent_id);
    return {
      ...conversation,
      agent_name: agent?.name ?? unknownAgentLabel,
    };
  });
};

const renderTopAgentsSkeleton = (count: number) => {
  return Array.from({ length: count }, () => (
    <Stack key={generateUniqueKey()} direction="row" gap="1rem" width="100%">
      <Skeleton variant="rounded" width="70%" height="250px" />
      <Stack direction="column" gap="1rem" width="30%">
        <Skeleton width="100%" />
        <Skeleton width="100%" />
        <Skeleton width="100%" />
        <Skeleton width="100%" />
      </Stack>
    </Stack>
  ));
};

const MultiChatAgentsDashboard: React.FC<MultiChatAgentsDashboardProps> = ({
  dateRange,
  selectedAgent,
  showLimitModal,
  setShowLimitModal,
  onRefetchNeeded,
  onLoadingStateChange,
}) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  const table = useDashboardTableState({
    selectedAgent,
    dateRange,
    agentType: AgentType.CHAT,
  });

  // Fetch chat agents list
  const { data: agentsList } = useDashboardChatAgents(!!currentUser);

  // Only the picked agent, not every agent in the company
  const agentIds = useMemo(() => {
    if (!table.isAllAgents) {
      return selectedAgent;
    }
    return agentsList
      .map((a: ChatAgent) => a.id)
      .filter(Boolean)
      .join(',');
  }, [agentsList, selectedAgent, table.isAllAgents]);

  // Fetch top chat agents
  const { data: topChatAgentsData, isLoading: isLoadingTopAgents } =
    useTopChatAgents({
      companyId: currentUser?.company_id,
      userId: currentUser?.id,
      startDate: dateRange[0]?.format('YYYY-MM-DD'),
      endDate: dateRange[1]?.format('YYYY-MM-DD'),
      agentsList: agentsList,
    });

  // Fetch chat dashboard data
  const {
    data: rawChatDashboardData,
    refetch: refetchChatDashboardData,
    isLoading: isLoadingDashboard,
    isFetching: isFetchingDashboard,
    error: dashboardError,
  } = useChatDashboardData({
    companyId: currentUser?.company_id,
    userId: currentUser?.id,
    startDate: dateRange[0]?.format('YYYY-MM-DD'),
    endDate: dateRange[1]?.format('YYYY-MM-DD'),
    agentIds,
    categories: table.sortedCategories,
    paging: table.paging,
  });
  useErrorToast(dashboardError);
  // The cards change with the company, the agents, the range and the chips.
  // Not with a page, a sort or the search box. Same fields as the query's
  // data set, so the agent list arriving late counts as a change too.
  const isRefreshingCards = useIsRefreshing(
    [
      currentUser?.company_id,
      currentUser?.id,
      dateRange[0]?.format('YYYY-MM-DD'),
      dateRange[1]?.format('YYYY-MM-DD'),
      agentIds,
      ...table.sortedCategories,
    ].join('|'),
    isFetchingDashboard
  );

  // Expose refetch to parent for live sync
  useEffect(() => {
    if (onRefetchNeeded) {
      onRefetchNeeded(refetchChatDashboardData);
    }
  }, [onRefetchNeeded, refetchChatDashboardData]);

  // Expose loading state to parent for LiveSyncToggle disabled state
  useEffect(() => {
    if (onLoadingStateChange) {
      onLoadingStateChange(isLoadingDashboard);
    }
  }, [onLoadingStateChange, isLoadingDashboard]);

  // Guard only, the request is already scoped to the picked agent
  const filteredData = useMemo(() => {
    if (!rawChatDashboardData) {
      return null;
    }
    if (table.isAllAgents) {
      return rawChatDashboardData;
    }
    return filteredChatDashboardData(rawChatDashboardData, selectedAgent);
  }, [rawChatDashboardData, selectedAgent, table.isAllAgents]);

  // Get conversations with agent names
  const conversations = useMemo(() => {
    if (!filteredData || !agentsList) {
      return [];
    }
    return fillOutAgentNames(
      getUniqueChatConversations(filteredData),
      agentsList,
      t('Unknown Agent')
    );
  }, [filteredData, agentsList, t]);

  const { categoryCounts, categoryOptions } = useCategoryChips({
    data: filteredData,
    isFetching: isFetchingDashboard,
    dataSetKey: table.dataSetKey,
    setSelectedCategories: table.setSelectedCategories,
  });

  // Totals, chart and rows come back already filtered by chips and search
  const agentsOverAllMetrics = useMemo(() => {
    if (!filteredData) {
      return null;
    }
    return {
      averageSatisfaction: getAverageSatisfaction(filteredData),
      totalConversations: getTotalConversations(filteredData),
      totalMessages: getTotalMessages(filteredData),
      averageSeconds: getAverageSeconds(filteredData),
      averageMessages: getAverageMessages(filteredData),
    };
  }, [filteredData]);

  const conversationsPerMonth = useMemo(
    () => (filteredData ? getConversationsPerMonth(filteredData) : {}),
    [filteredData]
  );

  const conversationsPerDay = useMemo(
    () => getCountsPerDay(filteredData, 'daily_conversations_number'),
    [filteredData]
  );

  // Filter top chat agents by selected agent
  const topChatAgents = useMemo(() => {
    if (!topChatAgentsData) {
      return [];
    }
    if (table.isAllAgents) {
      return topChatAgentsData;
    }
    const selectedAgentName = agentsList?.find(
      a => a.id === selectedAgent
    )?.name;
    return topChatAgentsData.filter(
      agent => agent.agentName === selectedAgentName
    );
  }, [topChatAgentsData, selectedAgent, agentsList, table.isAllAgents]);

  // Check if we should show the agents section (need at least 2 agents)
  const [shouldShowAgentsSection, setShouldShowAgentsSection] = useState(false);

  useEffect(() => {
    // Wait for loading to complete before making decision
    if (isLoadingTopAgents) {
      setShouldShowAgentsSection(false);
      return;
    }

    // Check topChatAgents length - these are the agents that actually have data
    // and are filtered by the current type (CALL/CHAT)
    if (!topChatAgents || !Array.isArray(topChatAgents)) {
      setShouldShowAgentsSection(false);
      return;
    }

    // Only show if we have at least 2 top chat agents
    const hasEnoughAgents = topChatAgents.length >= 2;
    setShouldShowAgentsSection(hasEnoughAgents);
  }, [topChatAgents, isLoadingTopAgents]);

  return (
    <>
      {/* Agents section - only show if there are 2+ agents */}
      {shouldShowAgentsSection && (
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            overflowX: 'auto',
            p: { xs: 1.25, md: 2 },
            mb: 2,
            borderRadius: 3,
            border: '1px solid rgba(15, 23, 42, 0.06)',
            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
          }}
        >
          <Typography component="h2" variant="h5" sx={{ mb: 1.5 }}>
            {t('Top 3 Best {{type}} Agents', {
              type: t('Chat'),
            })}
          </Typography>
          <StyledTopThreeAgents>
            {!isLoadingTopAgents && topChatAgents?.length === 0 && (
              <span>
                {t('Top {{type}} agents data not found.', {
                  type: t('chat'),
                })}
              </span>
            )}
            {!isLoadingTopAgents &&
              topChatAgents?.map(
                (
                  {
                    agentName,
                    image,
                    conversations,
                    messages,
                    averageSatisfaction,
                  },
                  idx
                ) => (
                  <ChatAgentTopThree
                    key={idx}
                    agentName={agentName}
                    image={image}
                    conversations={conversations}
                    messages={messages}
                    averageSatisfaction={averageSatisfaction}
                  />
                )
              )}
            {isLoadingTopAgents && renderTopAgentsSkeleton(3)}
          </StyledTopThreeAgents>
        </Paper>
      )}
      {/* Rest of content - full width */}
      <Stack width="100%" direction="column">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.25, md: 2 },
            borderRadius: 3,
            border: '1px solid rgba(15, 23, 42, 0.06)',
            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
          }}
        >
          <ChatOverAllMetrics
            {...agentsOverAllMetrics}
            isRefreshing={isRefreshingCards}
          />
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.25, md: 2 },
            mt: 2,
            borderRadius: 3,
            border: '1px solid rgba(15, 23, 42, 0.06)',
            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
          }}
        >
          <BarChart
            agentType={AgentType.CHAT}
            chatPerMonth={conversationsPerMonth}
            perDay={conversationsPerDay}
            dateRange={dateRange}
          />
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.25, md: 2 },
            mt: 2,
            borderRadius: 3,
            border: '1px solid rgba(15, 23, 42, 0.06)',
            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography component="h3" variant="h6" sx={{ m: 0 }}>
              {t('Conversations')}
            </Typography>
          </Box>
          {/* Category chips. Picking one refetches with the backend filter. */}
          <CategoryFilter
            categories={categoryOptions}
            categoryCounts={categoryCounts}
            selectedCategories={table.selectedCategories}
            onChange={table.setSelectedCategories}
          />
          {/* Keyed by agent, a switch starts a fresh grid */}
          <ConversationsTable
            key={selectedAgent}
            conversations={conversations}
            agentType={AgentType.CHAT}
            serverPaging={table.serverPaging(
              getTotalRows(
                filteredData,
                agentsOverAllMetrics?.totalConversations ?? 0
              ),
              isFetchingDashboard
            )}
          />
        </Paper>
        <LiveSyncStoppedModal
          open={showLimitModal}
          setOpen={setShowLimitModal}
        />
      </Stack>
    </>
  );
};

export default MultiChatAgentsDashboard;
